import React from 'react';
import styled from 'styled-components';
import {StatusChip, type StatusTone} from '../components/StatusChip';
import {INK} from '../onboarding/review/reviewStyles';
import type {StepState} from './forwardFlowServicingApi';

/** Shared surfaces for the Assets, Collections and Monitoring workspaces. */
export const Pane = styled.section`
  border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel}; padding: 20px; margin: 0 0 16px;
  > header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 12px; }
  > header h2 { margin: 0; font-size: 16px; }
  > header p { margin: 4px 0 0; color: ${INK.muted}; font-size: 13px; line-height: 1.5; }
  p { color: ${INK.muted}; font-size: 13px; line-height: 1.55; }
  label { display: grid; gap: 5px; color: ${INK.soft}; font-size: 12px; font-weight: 600; }
  input, select, textarea { box-sizing: border-box; width: 100%; min-height: 38px; padding: 7px 10px; color: ${INK.body};
    background: ${INK.well}; border: 1px solid ${INK.lineStrong}; border-radius: 7px; font: inherit; font-size: 13px; }
  textarea { min-height: 70px; resize: vertical; }
  .muted { color: ${INK.muted}; }
  .num { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
`;

export const Table = styled.table`
  border-collapse: collapse; width: 100%; font-size: 13px;
  th, td { padding: 9px 8px; border-bottom: 1px solid ${INK.line}; text-align: left; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; font-weight: 700; white-space: nowrap; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td { font-weight: 700; border-top: 1px solid ${INK.lineStrong}; border-bottom: 0; }
  tr[data-attention='true'] td:first-child { box-shadow: inset 3px 0 #f6c453; }
  tbody tr:hover td { background: #11233a; }
  button.link { border: 0; padding: 0; background: transparent; color: ${INK.accentSoft}; cursor: pointer; font: inherit; text-align: left; }
  button.link:hover { text-decoration: underline; }
`;

export const Scroll = styled.div`overflow-x: auto;`;

export const Facts = styled.div`
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 1px; margin: 0 0 16px;
  border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden; background: ${INK.line};
  > div { background: ${INK.raised}; padding: 13px 15px; min-width: 0; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
  strong { display: block; margin-top: 5px; font-size: 18px; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  small { display: block; margin-top: 3px; color: ${INK.muted}; font-size: 12px; }
  @media (max-width: 1100px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

export const StepRail = styled.ol`
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); list-style: none; padding: 0; margin: 0 0 16px;
  border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { padding: 12px 14px; background: ${INK.panel}; min-width: 0; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  button { all: unset; cursor: pointer; display: block; width: 100%; }
  button:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
  strong { display: block; font-size: 13px; margin: 6px 0 2px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; line-height: 1.4; }
  @media (max-width: 900px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid ${INK.line}; } }
`;

export const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 9px; align-items: center;`;
export const FormGrid = styled.div<{$cols?: number}>`
  display: grid; grid-template-columns: repeat(${p => p.$cols ?? 4}, minmax(0, 1fr)); gap: 12px; margin: 10px 0 14px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

export const Callout = styled.div<{$tone?: 'info' | 'warn' | 'error' | 'ok'}>`
  padding: 12px 14px; border-radius: 9px; margin: 0 0 14px; font-size: 13px; line-height: 1.55;
  border: 1px solid ${p => p.$tone === 'error' ? '#7f2d2d' : p.$tone === 'warn' ? '#7a5a1a' : p.$tone === 'ok' ? '#1f5d47' : INK.lineStrong};
  background: ${p => p.$tone === 'error' ? '#2a1015' : p.$tone === 'warn' ? '#261d0c' : p.$tone === 'ok' ? '#0c2219' : INK.raised};
  color: ${p => p.$tone === 'error' ? '#fecaca' : p.$tone === 'warn' ? '#fde68a' : p.$tone === 'ok' ? '#bbf7d0' : INK.soft};
  strong { color: inherit; }
  ul { margin: 6px 0 0 18px; padding: 0; }
`;

export const StageDashes = styled.span`
  display: inline-flex; gap: 3px; margin: 6px 0 3px;
  i { display: block; width: 22px; height: 4px; border-radius: 2px; background: #2c3e54; }
  i[data-state='DONE'] { background: #34d399; }
  i[data-state='READY'], i[data-state='PROGRESS'] { background: #38bdf8; }
  i[data-state='BLOCKED'] { background: #f87171; }
`;

const STEP_TONE: Record<string, StatusTone> = {DONE: 'done', READY: 'progress', PROGRESS: 'progress', WAITING: 'neutral', BLOCKED: 'critical'};
const STEP_WORD: Record<string, string> = {DONE: 'Done', READY: 'Ready', PROGRESS: 'In progress', WAITING: 'Waiting', BLOCKED: 'Blocked'};
export const StepChip = ({state}: {state: StepState | string}) =>
  <StatusChip tone={STEP_TONE[state] ?? 'neutral'} label={STEP_WORD[state] ?? String(state)} />;

const STATE_TONE: Record<string, StatusTone> = {
  OPEN: 'progress', SIGNED_OFF: 'done', RESTATING: 'warning', RESOLVED: 'done', CLEARED: 'neutral',
  RELEASED: 'done', VALIDATED: 'progress', INVALID: 'critical', SUPERSEDED: 'neutral',
  RECORDED: 'progress', CONFIRMED: 'done', RETURNED: 'critical', VOIDED: 'neutral',
  RUNNING: 'progress', COMPLETED: 'done', PARTIAL: 'warning', CALCULATED: 'progress', REVIEWED: 'progress',
  CHALLENGED: 'warning', APPROVED: 'done', REJECTED: 'critical', ATTESTED: 'progress',
  BREAK: 'critical', TIMING: 'warning', EXPLAINED: 'done', POSTED: 'done', FAILED: 'critical', STALE: 'warning', PROPOSED: 'neutral',
  BOOK: 'progress', REBOOK: 'warning', UNCHANGED: 'done', HELD: 'critical',
  ACTIVE: 'progress', PAID_OFF: 'done', CHARGED_OFF: 'critical', REPURCHASED: 'neutral', CLOSED: 'neutral',
  BOOKED: 'done', REVERSED: 'neutral', ON_TIME: 'done', LATE: 'warning', AWAITED: 'neutral',
  SETTLED: 'done', POSTED_UNPAID: 'warning', DEFERRED_DUE: 'warning', TRANSFER_CONFIRMED: 'progress',
};
const STATE_WORD: Record<string, string> = {
  OPEN: 'Open', SIGNED_OFF: 'Signed off', RESTATING: 'Restating', RESOLVED: 'Resolved', CLEARED: 'Cleared',
  RELEASED: 'Released', VALIDATED: 'Validated', INVALID: 'Has errors', SUPERSEDED: 'Superseded',
  RECORDED: 'Awaiting confirmation', CONFIRMED: 'Confirmed', RETURNED: 'Returned', VOIDED: 'Voided',
  RUNNING: 'Booking', COMPLETED: 'Booked', PARTIAL: 'Partly booked', CALCULATED: 'Awaiting review', REVIEWED: 'Awaiting approval',
  CHALLENGED: 'Challenged', APPROVED: 'Approved', REJECTED: 'Rejected', ATTESTED: 'Awaiting countersignature',
  BREAK: 'Break', TIMING: 'Timing', EXPLAINED: 'Explained', POSTED: 'Posted', FAILED: 'Failed', STALE: 'Stale', PROPOSED: 'Proposed',
  BOOK: 'To book', REBOOK: 'To correct', UNCHANGED: 'Booked', HELD: 'Held',
  ACTIVE: 'Active', PAID_OFF: 'Paid off', CHARGED_OFF: 'Charged off', REPURCHASED: 'Repurchased', CLOSED: 'Closed',
  BOOKED: 'Booked', REVERSED: 'Reversed', ON_TIME: 'On time', LATE: 'Late', AWAITED: 'Awaited',
  SETTLED: 'Paid', POSTED_UNPAID: 'Payable open', DEFERRED_DUE: 'Holdback due', TRANSFER_CONFIRMED: 'Transfer confirmed',
};
export const StateChip = ({state}: {state?: string | null}) =>
  <StatusChip tone={STATE_TONE[state ?? ''] ?? 'neutral'} label={STATE_WORD[state ?? ''] ?? (state ?? '—')} />;

export const Empty = ({title, children}: {title: string; children?: React.ReactNode}) =>
  <Pane><header><div><h2>{title}</h2>{children && <p>{children}</p>}</div></header></Pane>;
